import { Algorithm } from 'rheostat';
import { RangeSectionScale } from '../../../../../../types';

/**
 * Factory function to create the kind of Algorithm object Rheostat expects.
 * Accepts one function to transform the actual value to the range scale, and one
 * function to get back the original value from the scaled value.
 */
const makeScalingAlgorithm = (
  getScaledValue: (originalValue: number) => number,
  getOriginalValue: (scaledValue: number) => number
): Algorithm => ({
  getPosition(value, min, max) {
    const minValue = getScaledValue(min);
    const maxValue = getScaledValue(max);
    const scale = (maxValue - minValue) / 100;

    return (getScaledValue(value) - minValue) / scale;
  },

  getValue(positionPercent, min, max) {
    if (positionPercent === 0) {
      return min;
    }

    if (positionPercent === 100) {
      return max;
    }

    const minValue = getScaledValue(min);
    const maxValue = getScaledValue(max);
    const scale = (maxValue - minValue) / 100;

    return (
      Math.floor(getOriginalValue(minValue + scale * positionPercent)) || 0
    );
  },
});

const log2: Algorithm = makeScalingAlgorithm(
  value => Math.log2(value),
  scaledValue => 2 ** scaledValue
);

const log10: Algorithm = makeScalingAlgorithm(
  value => Math.log10(value),
  scaledValue => 10 ** scaledValue
);

const unsupportedScale = (_scale: never) => undefined;

export const getAlgorithmByScale = (
  scale: RangeSectionScale
): Algorithm | undefined => {
  switch (scale) {
    case 'log2':
      return log2;
    case 'log10':
      return log10;
    case 'linear':
      // Undefined here means using Rheostat's default (linear) algorithm
      return undefined;
    default:
      // This helper makes sure the switch statement is exhaustive
      return unsupportedScale(scale);
  }
};
